import { useCallback, useEffect, useReducer, useState } from "react";
import { IVerifyTaskEmailReviewRes } from "@evennt-tech/api-v2-external";

import { apiV2 } from "api/firebase";
import parseURLParams from "./parseURLParams";

// The Task PDF is rendered by an async backend process after an approval, so
// it isn't in the verify response immediately. This page can't use realtime
// (the reviewer is anonymous — no socket session), so we poll the verify
// endpoint until the generated `printTaskFile` shows up. Bounded so a verdict
// that never produces a PDF (or a slow/failed render) doesn't poll forever.
const PDF_POLL_INTERVAL_MS = 4000;
const PDF_POLL_MAX_ATTEMPTS = 15; // ~60s

/**
 * Anonymous email-review loader. The key is shared per (show, reviewer
 * email), so `taskId` + `standId` are forwarded to pin the resolve
 * target when the reviewer has several tasks.
 */
type Loaded = {
  data?: IVerifyTaskEmailReviewRes<string>;
  isLoading: boolean;
  error?: string;
  // The BE rejected the key itself (invalid / expired / not found) — the
  // reviewer should be steered to the resend-link recovery flow, not shown
  // a blank page. Transient 5xx / network errors do NOT set this.
  isStaleKey?: boolean;
};

// New magic-link keys are 64-char hex (HMAC-SHA256). Legacy pre-FS-2679 links
// carry a UUID, which the BE DTO rejects with a 400 before any lookup — so the
// format check below short-circuits those without a wasted round-trip.
const VALID_REVIEW_KEY = /^[0-9a-f]{64}$/i;

// A key the BE can't honour:
//   - 400 — DTO validation (legacy UUID: wrong length / not hex)
//   - 404 (TASK_REVIEW_KEY_INVALID) / 410 (TASK_REVIEW_KEY_EXPIRED)
// All mean "show the resend-link recovery flow, not a blank page". Match on
// the BE error code too in case the SDK reshapes the status.
const isStaleKeyError = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  const code = JSON.stringify(
    (err as { response?: { data?: unknown } })?.response?.data ??
      (err as Error)?.message ??
      ""
  );
  return (
    status === 400 ||
    status === 404 ||
    status === 410 ||
    /TASK_REVIEW_KEY_(INVALID|EXPIRED|USED)/.test(code) ||
    /key must be|hexadecimal/i.test(code)
  );
};

const useTaskReview = () => {
  const [trigger, forceUpdate] = useReducer((x) => x + 1, 0);
  const { showId, key, taskId, standId } = parseURLParams();
  const [value, setValue] = useState<Loaded>({ isLoading: false });
  // >0 while we're polling for the async-generated Task PDF; counts down.
  const [pdfPollAttemptsLeft, setPdfPollAttemptsLeft] = useState(0);

  useEffect(() => {
    if (!showId || !key) return;

    // Legacy UUID key — the BE would only 400. Flag it stale up front so the
    // page renders the resend-link recovery modal.
    if (!VALID_REVIEW_KEY.test(key)) {
      setValue({ isLoading: false, isStaleKey: true });
      return;
    }

    const loader = async () => {
      // Preserve any already-loaded data while (re)fetching so a post-verdict
      // refetch doesn't blank the task card — the reviewer stays on the page.
      setValue((prev) => ({ ...prev, isLoading: true }));
      try {
        const data = await apiV2.task.verifyTaskEmailReview({
          showId,
          key,
          ...(taskId && standId ? { taskId, standId } : {}),
        });
        setValue({ data, isLoading: false, error: undefined });
      } catch (err) {
        setValue({
          isLoading: false,
          error: `Error during getting task to review: ${
            (err as Error)?.message ?? err
          }`,
          isStaleKey: isStaleKeyError(err),
        });
      }
    };

    loader();
  }, [showId, key, taskId, standId, trigger]);

  // PDF polling loop: while attempts remain and the PDF hasn't landed, refetch
  // on an interval. Stops as soon as `printTaskFile` appears or attempts run
  // out. `forceUpdate` re-runs the loader above.
  useEffect(() => {
    if (pdfPollAttemptsLeft <= 0) return undefined;
    if (value.data?.printTaskFile) {
      setPdfPollAttemptsLeft(0);
      return undefined;
    }
    const timer = setTimeout(() => {
      setPdfPollAttemptsLeft((left) => left - 1);
      forceUpdate();
    }, PDF_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [pdfPollAttemptsLeft, value.data?.printTaskFile]);

  // Kick off polling after a verdict that is expected to produce a Task PDF
  // (approval). No-op-safe to call repeatedly — it just restarts the window.
  const pollForPdf = useCallback(() => {
    setPdfPollAttemptsLeft(PDF_POLL_MAX_ATTEMPTS);
  }, []);

  return [
    value.data,
    value.isLoading,
    value.error,
    forceUpdate,
    value.isStaleKey,
    pollForPdf,
  ] as const;
};

export default useTaskReview;
